import Axios from 'axios';
import { Branch } from './branchApi';
import { Client } from './clientsApi';
import { RotatedPhotoDetailsInterface, UploadPhoto } from './inspectionsApi';
import base64ImageToBlob from 'util/base64ImageToBlob';
import { User } from './userApi';
import { OtherExpenseCategory } from './otherExpensesApi';
import { postRateImportFile as postRateImportFileShared, downloadRateTemplate, sanitizeFilenamePart } from './rateImportApi';

export const ConsumableUnitOptions = [
  'kg',
  'l',
  'pcs',
  'per day',
  'm',
  'sq m',
  'ml',
] as const;

export type ConsumableItemType = {
  id: string;
  name: string;
  unitType: string;
};

export type ConsumableBuyAndSellRateType = {
  id: string;
  organizationId: number;
  consumableItemId: string;
  branchId: string;
  branch: Branch;
  buyRate: string;
  sellRate: string;
  effectiveFrom: string;
  createdOn: string;
  updatedOn: string;
  client?: Client;
  clientId?: string;
  name?: string;
  isDefault: boolean;
};

export type ConsumableRateFormType = {
  name: string;
  sellRate?: number;
  country?: string;
  state?: string;
  branchId?: string;
  effectiveFrom: string;
  clientId: string;
  consumableItemId: string;
  buyRate?: number;
  isDefault: boolean;
  client: Client;
  branch: Branch;
  id: string;
};

export type ConsumableItem = {
  id: string;
  name: string;
  unitType: string;
  tags?: string[];
  rank?: number;
  sellRates?: ConsumableBuyAndSellRateType[];
  buyRates?: ConsumableBuyAndSellRateType[];
  isDeleted?: boolean;
};

export enum RateType {
  BuyRate = 'buyRate',
  SellRate = 'sellRate',
}

export enum RateTab {
  Equipment = 'equipment',
  Consumables = 'consumables',
  Labour = 'labour',
  MarginSummary = 'margin-summary',
  OtherExpenses = 'other-expenses',
}

export async function listConsumableItems(): Promise<ConsumableItem[]> {
  const res = await Axios.get(`/api/v1/consumable`);
  return res.data;
}

export async function getConsumableDetailsById(
  id: string
): Promise<ConsumableItem> {
  const res = await Axios.get(`/api/v1/consumable/${id}`);
  return res.data;
}

export async function addConsumable({ data }: { data: ConsumableItemType }) {
  const res = await Axios.post(`/api/v1/consumable`, data);
  return res.data;
}

export async function addBuyRate({ data }: { data: ConsumableRateFormType }) {
  const res = await Axios.post(`/api/v1/consumable/buy-rate`, data);
  return res.data;
}

export async function addSellRate({ data }: { data: ConsumableRateFormType }) {
  const res = await Axios.post(`/api/v1/consumable/sell-rate`, data);
  return res.data;
}

export async function listBuyRates(
  consumableItemId: string
): Promise<ConsumableRateFormType[]> {
  const res = await Axios.get(
    `/api/v1/consumable/${consumableItemId}/buy-rates`
  );
  return res.data;
}

export async function listSellRates(
  consumableItemId: string
): Promise<ConsumableRateFormType[]> {
  const res = await Axios.get(
    `/api/v1/consumable/${consumableItemId}/sell-rates`
  );
  return res.data;
}

export async function updateRate({
  id,
  data,
  rateType,
}: {
  id: string;
  data: Partial<ConsumableRateFormType>;
  rateType: 'buyRate' | 'sellRate';
}) {
  const res = await Axios.put(`/api/v1/consumable/${rateType}/${id}`, data);
  return res.data;
}

export async function updateSingleConsumableItem({
  id,
  data,
}: {
  id: string | number;
  data: Partial<ConsumableItemType>;
}) {
  const res = await Axios.put(`/api/v1/consumable/${id}`, data);
  return res.data;
}

export async function deleteSellRate(id: string) {
  try {
    const response = await Axios.delete(`/api/v1/consumable/sell-rates/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete sell rate: ${error}`);
  }
}

export async function deleteConsumableItem(id: string) {
  try {
    const response = await Axios.delete(`/api/v1/consumable/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete consumable item: ${error}`);
  }
}

export async function deleteBuyRate(id: string) {
  try {
    const response = await Axios.delete(`/api/v1/consumable/buy-rates/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete buy rate: ${error}`);
  }
}

export type DefaultRateFieldPlan = {
  action: 'insert' | 'update' | 'unchanged';
  newValue: number;
  oldValue?: number;
  rateId?: string;
  effectiveFrom: string;
  dateStatus: 'past' | 'today' | 'future';
  dateMoved?: boolean;
};

export type DefaultRateUnitPlan = {
  action: 'create' | 'update' | 'unchanged';
  newValue: string;
  oldValue?: string;
};

export type DefaultRateRowPlan = {
  row: number;
  name: string;
  consumableItemId: string;
  itemAction: 'create' | 'existing';
  unit?: DefaultRateUnitPlan;
  buy?: DefaultRateFieldPlan;
  sell?: DefaultRateFieldPlan;
};

export type { ImportMode } from './rateImportApi';

export type BulkUpdateDefaultRatesResponse = {
  committed: boolean;
  success: boolean;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  ignoredCount: number;
  errorCount: number;
  created: DefaultRateRowPlan[];
  updated: DefaultRateRowPlan[];
  ignored: { row: number; name?: string; reason: string }[];
  errors: { row: number; name?: string; message: string }[];
  previewToken?: string;
};

function postRateImportFile(
  url: string,
  file: File,
  extraFields?: Record<string, string>
): Promise<BulkUpdateDefaultRatesResponse> {
  return postRateImportFileShared<BulkUpdateDefaultRatesResponse>(url, file, extraFields);
}

export async function compareConsumableDefaultRatesImport({
  file,
}: {
  file: File;
}): Promise<BulkUpdateDefaultRatesResponse> {
  return postRateImportFile(
    '/api/v1/consumable/default-rates/import/compare/new',
    file
  );
}

export async function compareConsumableDefaultRatesUpdateImport({
  file,
}: {
  file: File;
}): Promise<BulkUpdateDefaultRatesResponse> {
  return postRateImportFile(
    '/api/v1/consumable/default-rates/import/compare/update',
    file
  );
}

// Reuses the existing import plan instead of rebuilding it
export async function bulkUpdateConsumableDefaultRates({
  previewToken,
}: {
  previewToken: string;
}): Promise<BulkUpdateDefaultRatesResponse> {
  const res = await Axios.post(`/api/v1/consumable/default-rates/import`, {
    previewToken,
  });

  return res.data;
}

// Buy-only template, scoped to one branch
export async function compareConsumableBranchBuyRateImport({
  file,
  branchId,
}: {
  file: File;
  branchId: string;
}): Promise<BulkUpdateDefaultRatesResponse> {
  return postRateImportFile(
    '/api/v1/consumable/rates/buy/import/compare/new',
    file,
    { branchId }
  );
}

export async function compareConsumableBranchBuyRateUpdateImport({
  file,
  branchId,
}: {
  file: File;
  branchId: string;
}): Promise<BulkUpdateDefaultRatesResponse> {
  return postRateImportFile(
    '/api/v1/consumable/rates/buy/import/compare/update',
    file,
    { branchId }
  );
}

// Sell-only template, scoped to a branch, a client, or both
export async function compareConsumableBranchClientSellRateImport({
  file,
  branchId,
  clientId,
}: {
  file: File;
  branchId?: string;
  clientId?: string;
}): Promise<BulkUpdateDefaultRatesResponse> {
  const extraFields: Record<string, string> = {};
  if (branchId) extraFields.branchId = branchId;
  if (clientId) extraFields.clientId = clientId;
  return postRateImportFile(
    '/api/v1/consumable/rates/sell/import/compare/new',
    file,
    extraFields
  );
}

export async function compareConsumableBranchClientSellRateUpdateImport({
  file,
  branchId,
  clientId,
}: {
  file: File;
  branchId?: string;
  clientId?: string;
}): Promise<BulkUpdateDefaultRatesResponse> {
  const extraFields: Record<string, string> = {};
  if (branchId) extraFields.branchId = branchId;
  if (clientId) extraFields.clientId = clientId;
  return postRateImportFile(
    '/api/v1/consumable/rates/sell/import/compare/update',
    file,
    extraFields
  );
}

// Reuses the previewed plan
export async function bulkUpdateConsumableScopedRates({
  previewToken,
}: {
  previewToken: string;
}): Promise<BulkUpdateDefaultRatesResponse> {
  const res = await Axios.post(`/api/v1/consumable/rates/import`, {
    previewToken,
  });

  return res.data;
}

export async function downloadConsumableDefaultRatesNewTemplate() {
  return downloadRateTemplate(
    '/api/v1/consumable/default-rates/import/template/new',
    'restorize_consumables_rates_default_new_template.xlsx'
  );
}

export async function downloadConsumableDefaultRatesUpdateTemplate() {
  return downloadRateTemplate(
    '/api/v1/consumable/default-rates/import/template/update',
    'restorize_consumables_rates_default_update_template.xlsx'
  );
}

export function getBranchBuyRatesTemplateFilename(
  mode: ImportMode,
  branchName: string
): string {
  return `restorize_consumables_rates_buy_${mode}_${sanitizeFilenamePart(branchName)}_template.xlsx`;
}

export function getSellRatesTemplateFilename(
  mode: ImportMode,
  branchName?: string,
  clientName?: string
): string {
  const scopeParts = [branchName, clientName].filter(Boolean).map(sanitizeFilenamePart);
  return `restorize_consumables_rates_sell_${mode}_${scopeParts.join('-')}_template.xlsx`;
}

export async function downloadConsumableBranchBuyRatesNewTemplate({
  branchId,
  branchName,
}: {
  branchId: string;
  branchName: string;
}) {
  return downloadRateTemplate(
    '/api/v1/consumable/rates/buy/import/template/new',
    getBranchBuyRatesTemplateFilename('new', branchName),
    { branchId }
  );
}

export async function downloadConsumableBranchBuyRatesUpdateTemplate({
  branchId,
  branchName,
}: {
  branchId: string;
  branchName: string;
}) {
  return downloadRateTemplate(
    '/api/v1/consumable/rates/buy/import/template/update',
    getBranchBuyRatesTemplateFilename('update', branchName),
    { branchId }
  );
}

export async function hasExistingBranchBuyRates({
  branchId,
}: {
  branchId: string;
}): Promise<boolean> {
  const res = await Axios.get('/api/v1/consumable/rates/buy/has-existing', {
    params: { branchId },
  });
  return res.data.exists;
}

export async function downloadConsumableSellRatesNewTemplate({
  branchId,
  clientId,
  branchName,
  clientName,
}: {
  branchId?: string;
  clientId?: string;
  branchName?: string;
  clientName?: string;
}) {
  return downloadRateTemplate(
    '/api/v1/consumable/rates/sell/import/template/new',
    getSellRatesTemplateFilename('new', branchName, clientName),
    { branchId, clientId }
  );
}

export async function downloadConsumableSellRatesUpdateTemplate({
  branchId,
  clientId,
  branchName,
  clientName,
}: {
  branchId?: string;
  clientId?: string;
  branchName?: string;
  clientName?: string;
}) {
  return downloadRateTemplate(
    '/api/v1/consumable/rates/sell/import/template/update',
    getSellRatesTemplateFilename('update', branchName, clientName),
    { branchId, clientId }
  );
}

export async function hasExistingSellRates({
  branchId,
  clientId,
}: {
  branchId?: string;
  clientId?: string;
}): Promise<boolean> {
  const res = await Axios.get('/api/v1/consumable/rates/sell/has-existing', {
    params: { branchId, clientId },
  });
  return res.data.exists;
}

export async function updateConsumableItemRanks({
  data,
}: {
  data: { id: string; rank: number }[];
}) {
  const res = await Axios.put(`/api/v1/consumable/ranks`, data);
  return res.data;
}

// Parking and Disposal APIs
export async function getParkingAndDisposal(
  jobId: string,
  inspectionId: string,
  expenseType: ExpenseType
): Promise<ParkingAndDisposalExpense[]> {
  const res = await Axios.get(
    `/api/v1/parking-and-disposal/job/${jobId}/inspection/${inspectionId}/expense-type/${expenseType}`
  );
  return res.data;
}

export async function createParkingAndDisposal({
  jobId,
  inspectionId,
  expenseType,
  otherExpenseCategoryId,
  amount,
  note,
  photos,
}: {
  jobId: string;
  inspectionId: string;
  expenseType: ExpenseType;
  // Required for ExpenseType.CUSTOM.
  otherExpenseCategoryId?: string;
  amount: number;
  note?: string;
  photos?: UploadPhoto[];
}): Promise<ParkingAndDisposalExpense> {
  const formData = new FormData();
  formData.append('amount', amount.toString());

  if (otherExpenseCategoryId) {
    formData.append('otherExpenseCategoryId', otherExpenseCategoryId);
  }

  if (note) {
    formData.append('note', note);
  }

  if (photos && photos.length > 0) {
    photos.forEach((photo: UploadPhoto) => {
      if (!photo.isDefault) {
        formData.append('photos', base64ImageToBlob(photo.gcsPath));
      }
      if (photo.id && photo.isDefault) {
        formData.append('photoIds', photo.id);
      }
    });
  }

  const res = await Axios.post(
    `/api/v1/parking-and-disposal/job/${jobId}/inspection/${inspectionId}/expense-type/${expenseType}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res.data;
}

export async function updateParkingAndDisposal({
  id,
  jobId,
  inspectionId,
  amount,
  note,
  photos,
  photosToDelete,
  rotatedPhotos,
}: {
  id: string;
  jobId: string;
  inspectionId: string;
  amount?: number;
  note?: string;
  photos?: UploadPhoto[];
  photosToDelete?: string[];
  rotatedPhotos?: RotatedPhotoDetailsInterface[] | null;
}): Promise<ParkingAndDisposalExpense> {
  const formData = new FormData();

  if (amount !== undefined) {
    formData.append('amount', amount.toString());
  }
  if (note !== undefined) {
    formData.append('note', note);
  }

  if (photos && photos.length > 0) {
    const photoIds: string[] = [];
    photos.forEach((photo: UploadPhoto) => {
      if (!photo.isDefault) {
        formData.append('photos', base64ImageToBlob(photo.gcsPath));
      }
      if (photo.isDefault && photo.id) {
        photoIds.push(photo.id);
      }
    });
    if (photoIds.length > 0) {
      formData.append('photoIds', JSON.stringify(photoIds));
    }
  }

  if (photosToDelete && photosToDelete.length > 0) {
    formData.append('photosToDelete', JSON.stringify(photosToDelete));
  }

  if (rotatedPhotos && rotatedPhotos.length > 0) {
    // Remove photos that are to be deleted
    if (photosToDelete) {
      rotatedPhotos = rotatedPhotos.filter((rotatedPhoto) => {
        return !photosToDelete.some(
          (photoToDelete) => photoToDelete === rotatedPhoto.id
        );
      });
    }
    formData.append('rotatedPhotoDetails', JSON.stringify(rotatedPhotos));
  }

  const res = await Axios.put(
    `/api/v1/parking-and-disposal/${id}/job/${jobId}/inspection/${inspectionId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res.data;
}

export async function deleteParkingAndDisposal(id: string): Promise<void> {
  const res = await Axios.delete(`/api/v1/parking-and-disposal/${id}`);
  return res.data;
}

// Specialized Trade APIs
export async function getSpecializedTrades(
  jobId: string,
  inspectionId: string
): Promise<SpecializedTradeData[]> {
  const res = await Axios.get(
    `/api/v1/specialized-trade/job/${jobId}/inspection/${inspectionId}`
  );
  return res.data;
}

export async function createSpecializedTrade({
  jobId,
  inspectionId,
  trades,
  amount,
  note,
  photos,
}: {
  jobId: string;
  inspectionId: string;
  trades: SpecializedTrade[];
  amount?: number;
  note?: string;
  photos?: UploadPhoto[];
}): Promise<SpecializedTradeData> {
  const formData = new FormData();
  formData.append('trades', JSON.stringify(trades));

  if (amount !== undefined && !Number.isNaN(amount)) {
    formData.append('amount', amount.toString());
  }

  if (note?.trim()) {
    formData.append('note', note.trim());
  }

  if (photos && photos.length > 0) {
    const photoIds: string[] = [];
    photos.forEach((photo) => {
      if (!photo.isDefault && photo.gcsPath?.startsWith('data:')) {
        const file = base64ImageToFile(photo.gcsPath, photo.id || 'photo');
        formData.append('photos', file, file.name);
        if (photo.id) {
          photoIds.push(photo.id);
        }
      }
    });

    if (photoIds.length > 0) {
      formData.append('photoIds', JSON.stringify(photoIds));
    }
  }

  const res = await Axios.post(
    `/api/v1/specialized-trade/job/${jobId}/inspection/${inspectionId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res.data;
}

export async function updateSpecializedTrade({
  id,
  jobId,
  inspectionId,
  trades,
  amount,
  note,
  photos,
  photosToDelete,
  rotatedPhotos,
}: {
  id: string;
  jobId: string;
  inspectionId: string;
  trades: SpecializedTrade[];
  amount?: number;
  note?: string;
  photos?: UploadPhoto[];
  photosToDelete?: Array<{ id: string; isDefault?: boolean }>;
  rotatedPhotos?: RotatedPhotoDetailsInterface[];
}): Promise<SpecializedTradeData> {
  const formData = new FormData();
  formData.append('trades', JSON.stringify(trades));

  if (amount !== undefined && !Number.isNaN(amount)) {
    formData.append('amount', amount.toString());
  }

  if (note !== undefined) {
    formData.append('note', note);
  }

  const photoIds: string[] = [];
  if (photos && photos.length > 0) {
    photos.forEach((photo) => {
      if (!photo.isDefault && photo.gcsPath?.startsWith('data:')) {
        const file = base64ImageToFile(photo.gcsPath, photo.id || 'photo');
        formData.append('photos', file, file.name);
        if (photo.id) {
          photoIds.push(photo.id);
        }
      }
    });
  }

  if (photoIds.length > 0) {
    formData.append('photoIds', JSON.stringify(photoIds));
  }

  if (photosToDelete && photosToDelete.length > 0) {
    formData.append('photosToDelete', JSON.stringify(photosToDelete));
  }

  if (rotatedPhotos && rotatedPhotos.length > 0) {
    formData.append('rotatedPhotoDetails', JSON.stringify(rotatedPhotos));
  }

  const res = await Axios.put(
    `/api/v1/specialized-trade/${id}/job/${jobId}/inspection/${inspectionId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res.data;
}

export async function deleteSpecializedTrade(id: string): Promise<void> {
  const res = await Axios.delete(`/api/v1/specialized-trade/${id}`);
  return res.data;
}

const base64ImageToFile = (base64: string, filename: string): File => {
  const arr = base64.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], `${filename}.jpg`, { type: mime });
};

export const buildSpecializedTradeKey = (trade: SpecializedTrade): string => {
  const type = trade.type.trim().toLowerCase();
  if (type === 'other') {
    return `other:${(trade.value || '').trim().toLowerCase()}`;
  }
  return `fixed:${type}`;
};

export const getSpecializedTradeLabel = (trade: SpecializedTrade): string => {
  if (trade.type.toLowerCase() === 'other') {
    return (trade.value || '').trim();
  }
  return trade.type;
};

const splitTradesIntoColumns = (trades: SpecializedTrade[]): string[][] => {
  const left: string[] = [];
  const right: string[] = [];

  trades.forEach((trade, index) => {
    const label = getSpecializedTradeLabel(trade);
    if (!label) {
      return;
    }

    if (index % 2 === 0) {
      left.push(label);
    } else {
      right.push(label);
    }
  });

  return [left, right];
};

export const getSpecializedTradeColumns = (
  trades: SpecializedTrade[]
): { left: string[]; right: string[] } => {
  const selectedTrades = trades.filter((trade) => trade.checked);
  const [left, right] = splitTradesIntoColumns(selectedTrades);
  return { left, right };
};

export const getSpecializedTradeDisplayCount = (
  trades: SpecializedTrade[]
): number => {
  return trades.filter((trade) => trade.checked).length;
};

export enum ExpenseType {
  PARKING = 'PARKING',
  DISPOSAL = 'DISPOSAL',
  SPECIALIZED_TRADE = 'SPECIALIZED_TRADE',
  OTHER = 'OTHER',
  // One of the organization's own categories, named by otherExpenseCategoryId.
  CUSTOM = 'CUSTOM',
}

export interface SpecializedTrade {
  type: string;
  value?: string;
  checked: boolean;
}

export interface ParkingAndDisposalExpense {
  id: string;
  organizationId: number;
  jobId: string;
  inspectionId: string;
  expenseType: ExpenseType;
  otherExpenseCategoryId?: string | null;
  otherExpenseCategory?: OtherExpenseCategory | null;
  amount: number;
  note?: string;
  createdOn: string;
  updatedOn: string;
  createdByUserId: string;
  updatedByUser?: User;
  attachments?: Array<{
    id: string;
    name: string;
    gcsPath: string;
    mimeType: string;
    fileByteSize: number;
  }>;
}

export interface SpecializedTradeData {
  id: string;
  organizationId: number;
  jobId: string;
  inspectionId: string;
  trades: SpecializedTrade[];
  amount?: number;
  note?: string;
  createdOn: string;
  updatedOn: string;
  createdByUser: User;
  updatedByUser: User;
  attachments?: Array<{
    id: string;
    name: string;
    gcsPath: string;
    mimeType: string;
  }>;
}
